import { FormEvent } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'

import classNames from 'classnames'

import {
  teacherEducationalQualificationOptions,
  teacherExperienceOptions,
  genderOptions,
} from '@common/enums'
import { ROLES } from '@lib/enums/dashboard'

import Input from '@components/common/forms/input'
import Checkbox from '@components/common/forms/checkbox'
import SelectInput from '@components/common/forms/select'
import TestimonialCard from '@components/auth/testimonial-card'
import DottedCardBackgroundWrapper from '@components/common/dotted-card-bg'
import TermsLabelComponent from '@components/common/terms-label'
import HasLaptopField from '@components/auth/has-laptop-field'
import { RecaptchaType } from '@lib/types/recaptcha'
import { childGradeOptions } from '@lib/enums/registration-form'

type Props = {
  phone: string
  name: string
  email: string
  role: string
  childName: string
  childGrade: { value: string; label: string }
  childGender: { value: string; label: string }
  teacherEducationalQualification: { value: string; label: string }
  teacherExperience: { value: string; label: string }
  teacherGender: { value: string; label: string }
  hasLaptop: boolean
  childConsent: boolean
  termsAndConditions: boolean
  whatsappEnabled: boolean
  loading: boolean
  errors: any
  recaptchaRef: React.MutableRefObject<RecaptchaType>
  handleCaptcha: (key: string) => void
  handleInputChange: (elem: FormEvent<HTMLInputElement>) => void
  handleCheckboxChange: (elem: FormEvent<HTMLInputElement>) => void
  handleSelectChange: (
    id: string,
    obj: {
      value: string
      label: string
    }
  ) => void
  handleRegistrationFormSubmit: (e: FormEvent<HTMLFormElement>) => void
}

const TestimonialCardWithDottedBackground = DottedCardBackgroundWrapper(
  TestimonialCard
)

export const RegistrationDetails = (props: Props): JSX.Element => {
  const submitBtnClass = classNames({
    'btn btn-orange w-full': true,
    'opacity-50 cursor-not-allowed': props.loading,
  })

  const { phone, role, errors } = props
  const { TEACHER, STUDENT } = ROLES

  return (
    <div className="flex flex-1 justify-between h-full items-center bg-white lg:bg-transparent">
      <div className="container mx-auto h-full">
        <div className="flex flex-wrap items-center h-full">
          <div className="hidden lg:flex items-center w-full lg:w-1/2 pr-5 h-full">
            <TestimonialCardWithDottedBackground />
          </div>
          <div className="w-full lg:w-1/2 px-4 max-w-md">
            <form
              className="px-5"
              onSubmit={props.handleRegistrationFormSubmit}
            >
              <ReCAPTCHA
                ref={props.recaptchaRef}
                size="invisible"
                sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
                onChange={props.handleCaptcha}
              />
              <div className="text-grey text-4xl lg:text-5xl font-600 leading-tight mb-2 pt-5">
                <p>Hey +{phone}</p>
              </div>
              <div className="text-grey text-lg font-600 my-2">
                We need your details to get started
              </div>
              <div className="mt-5">
                <Input
                  label={role === STUDENT ? 'Student name' : 'Name'}
                  id="name"
                  name="name"
                  placeholder="Name"
                  autoFocus={true}
                  value={props.name}
                  onChange={props.handleInputChange}
                  required
                  errorText={errors['name']}
                />
              </div>
              <div className="mt-4">
                <Input
                  label="Email"
                  id="email"
                  name="email"
                  type="email"
                  placeholder="you@mail.com"
                  value={props.email}
                  onChange={props.handleInputChange}
                  errorText={errors['email']}
                  required
                />
              </div>

              {role === TEACHER && (
                <div>
                  <div className="mt-4">
                    <SelectInput
                      label="Highest qualification"
                      id="teacherEducationalQualification"
                      value={props.teacherEducationalQualification}
                      handleSelectChange={props.handleSelectChange}
                      options={teacherEducationalQualificationOptions}
                      menuPlacement="top"
                      errorText={errors['teacherEducationalQualification']}
                      required
                    />
                  </div>
                  <div className="flex">
                    <div className="w-1/2 mt-4 pr-4">
                      <SelectInput
                        label="Past coding experience"
                        id="teacherExperience"
                        placeholder="Select"
                        value={props.teacherExperience}
                        handleSelectChange={props.handleSelectChange}
                        options={teacherExperienceOptions}
                        menuPlacement="top"
                        errorText={errors['teacherExperience']}
                        required
                      />
                    </div>
                    <div className="w-1/2 mt-4 pl-4">
                      <SelectInput
                        label="Gender"
                        id="teacherGender"
                        placeholder="Select"
                        value={props.teacherGender}
                        handleSelectChange={props.handleSelectChange}
                        options={genderOptions}
                        menuPlacement="top"
                        errorText={errors['teacherGender']}
                        required
                      />
                    </div>
                  </div>
                </div>
              )}

              {role === STUDENT && (
                <>
                  <div className="flex mt-4">
                    <div className="w-1/2 pr-4">
                      <SelectInput
                        label="Student grade"
                        id="childGrade"
                        placeholder="Select"
                        value={props.childGrade}
                        handleSelectChange={props.handleSelectChange}
                        options={childGradeOptions}
                        menuPlacement="top"
                        errorText={errors['childGrade']}
                        required
                      />
                    </div>
                    <div className="w-1/2 pl-4">
                      <SelectInput
                        label="Student gender"
                        id="childGender"
                        placeholder="Select"
                        value={props.childGender}
                        handleSelectChange={props.handleSelectChange}
                        options={genderOptions}
                        menuPlacement="top"
                        errorText={errors['childGender']}
                        required
                      />
                    </div>
                  </div>
                  <HasLaptopField
                    onChange={props.handleInputChange}
                    error={errors.hasLaptop}
                  />
                  <div className="flex flex-col mt-4">
                    <Input
                      label="Add voucher code (optional)"
                      id="voucher"
                      name="voucher"
                      placeholder="Voucher code"
                      onChange={props.handleInputChange}
                      errorText={errors['voucher']}
                    />
                  </div>
                </>
              )}

              {role === STUDENT && (
                <div className="mt-3">
                  <Checkbox
                    label="I certify that I have my parent's permission to use this platform"
                    id="childConsent"
                    name="childConsent"
                    checked={props.childConsent}
                    handleCheckboxChange={props.handleCheckboxChange}
                    errorText={errors['childConsent']}
                  />
                </div>
              )}

              <div className="mt-3">
                <Checkbox
                  id="whatsappEnabled"
                  name="whatsappEnabled"
                  labelComponent={
                    <p>
                      Receive updates and reminders on WhatsApp.
                      <br />
                      We promise not to spam you.
                    </p>
                  }
                  checked={props.whatsappEnabled}
                  handleCheckboxChange={props.handleCheckboxChange}
                  errorText={errors['whatsappEnabled']}
                />
              </div>

              <div className="mt-3">
                <Checkbox
                  labelComponent={TermsLabelComponent}
                  id="termsAndConditions"
                  name="termsAndConditions"
                  checked={props.termsAndConditions}
                  handleCheckboxChange={props.handleCheckboxChange}
                  errorText={errors['termsAndConditions']}
                />
              </div>

              <div className="my-5">
                <button className={submitBtnClass}>
                  {role === TEACHER
                    ? 'Complete your application'
                    : 'Proceed to book a free lesson'}
                </button>
              </div>
            </form>
            <div className="lg:hidden w-full lg:w-1/2 md:px-5 h-auto flex justify-center my-16">
              <TestimonialCard />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RegistrationDetails
